import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { UserTheme } from '@vd/types';
import { api } from '../lib/api';
import {
  captureAuthSession,
  isAuthSessionCurrent,
  registerSessionAbortController,
} from '../lib/authSessionEpoch';

/**
 * Light or dark, and which of the three settings asked for it.
 *
 * `UserTheme` is the setting and includes `'system'`. `Theme` is what got
 * resolved, and is what paints: `data-theme` on the document element only ever
 * says `light` or `dark`, because every stylesheet in the app keys off those
 * two and a third value would match none of them.
 *
 * The setting lives on the account (`users.theme`, `GET /me`), not only in
 * this browser, because the extension's in-page panel needs it too and cannot
 * read this origin's localStorage. localStorage is still written, as a cache:
 * it is what the first paint of a cold load has to go on, since `/me` has not
 * answered yet.
 */
export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'vd-theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

interface ThemeContextValue {
  /** What is painted right now. */
  theme: Theme;
  /** Which of the three settings produced it. */
  preference: UserTheme;
  /**
   * Choose a setting. Persists locally and, once the signed-in user's own
   * setting has arrived, to the account as well.
   */
  setPreference: (next: UserTheme) => void;
  /**
   * Flip between light and dark. Always lands on an explicit setting: someone
   * who reaches for a toggle is asking for a specific side, not for "follow
   * the machine".
   */
  toggle: () => void;
  /**
   * Adopt the setting the server holds for the signed-in user, or `null` when
   * nobody is signed in.
   *
   * Called by `ThemeSync` as `/me` answers and as the user signs in and out.
   * It is also what tells this provider WHO is signed in, which gates two
   * things: whether a change is written back at all (a `PATCH /me` from the
   * sign-in screen would 401) and which account it is written for.
   *
   * `null` leaves the current appearance alone - signing out should not
   * repaint the page on the way to the sign-in screen - and only stops
   * further writes.
   *
   * Once this page has made a choice for the signed-in user, later reads for
   * that SAME user are ignored. Not just while the write is in flight: a
   * `/me` that was issued before the choice can land after the PATCH has
   * committed, and it carries the old value, so gating on "is a write
   * outstanding" reverted the choice anyway - the UI went back while the
   * account stayed correct. Nothing on the response says how old it is, so
   * the rule is the one that cannot be wrong in the direction that matters:
   * the choice made HERE is the newest thing anyone knows about.
   *
   * A read for a DIFFERENT user always applies. That is not a stale read of
   * our setting, it is somebody else's setting, and whatever this page chose
   * belonged to the account that just went away.
   *
   * The cost is that a change made on another device does not reach an open
   * tab that has also changed it - it arrives on the next load. That is the
   * same staleness the setting had when it lived only in localStorage, and it
   * is the better half of the trade: a theme that quietly undoes itself is
   * the one people notice.
   */
  adoptServerPreference: (user: { id: string; theme?: UserTheme } | null) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function isUserTheme(value: unknown): value is UserTheme {
  return value === 'system' || value === 'light' || value === 'dark';
}

/**
 * What this browser last knew. Values written before `'system'` existed are
 * `'light'` or `'dark'`, which are still valid settings, so nothing has to be
 * migrated. Anything else, including nothing at all, means never chosen.
 */
function storedPreference(): UserTheme {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return isUserTheme(saved) ? saved : 'system';
  } catch {
    // Private-mode Safari throws on access rather than returning null.
    return 'system';
  }
}

function systemTheme(): Theme {
  return matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
}

export function resolveTheme(preference: UserTheme, system: Theme): Theme {
  return preference === 'system' ? system : preference;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<UserTheme>(storedPreference);
  const [system, setSystem] = useState<Theme>(systemTheme);
  /**
   * Who the writes belong to, and whether there is anyone to write for.
   *
   * Until `/me` has answered this is null and changes are local only. It also
   * stops the adopt-then-write loop: adopting the server's value must not
   * write it back.
   */
  const signedInAs = useRef<string | null>(null);
  /**
   * Whether this page has chosen for the signed-in user. See
   * `adoptServerPreference`.
   */
  const chosenHere = useRef(false);
  /**
   * One write at a time, in the order they were made.
   *
   * Two PATCHes in flight together can commit in either order, and the second
   * to commit wins - so picking dark and then light could persist dark. Each
   * write waits for the previous one and then checks whether it is still the
   * latest; a superseded write is dropped rather than sent, because sending a
   * value the person has already replaced is pure cost.
   */
  const writeTail = useRef<Promise<void>>(Promise.resolve());
  const writeSeq = useRef(0);

  const theme = resolveTheme(preference, system);

  // Following the machine means following it while the app is open, not only
  // at load. Someone on a sunset-scheduled OS theme gets the change here.
  useEffect(() => {
    const query = matchMedia(DARK_QUERY);
    const onChange = (event: MediaQueryListEvent) => setSystem(event.matches ? 'dark' : 'light');
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, preference);
    } catch {
      // Quota or private mode. The setting still applies for this page, and
      // the account copy is what makes it survive anyway.
    }
  }, [preference]);

  const setPreference = useCallback((next: UserTheme) => {
    setPreferenceState(next);
    const userId = signedInAs.current;
    if (userId === null) return;
    /**
     * Bound to the session this change was made in.
     *
     * Not belt and braces. The transport retries a 401 once after half a
     * second (see `fetchResponse` in apiClient), with whatever cookies are
     * current when it fires - so signing out and straight back in as somebody
     * else inside that window had the retry write the first account's choice
     * onto the second account. `captureAuthSession` returns null when the
     * session has already moved on, and the registered controller aborts the
     * request, and therefore its retry, if it moves on mid-flight.
     */
    const owner = captureAuthSession(userId);
    if (!owner) return;
    chosenHere.current = true;
    const seq = ++writeSeq.current;
    writeTail.current = writeTail.current.then(async () => {
      try {
        // Superseded while it waited. The newer value is the one to send.
        if (seq !== writeSeq.current) return;
        if (!isAuthSessionCurrent(owner)) return;
        const controller = new AbortController();
        const unregister = registerSessionAbortController(owner, controller);
        try {
          await api.updateMe({ theme: next }, { signal: controller.signal });
        } finally {
          unregister();
        }
      } catch {
        // Fire and forget. A theme that failed to save is a theme that comes
        // back on the next device, which is not worth a toast over whatever
        // the person was actually doing; the local copy still holds here.
        // Swallowed INSIDE the tail so one failure does not reject the chain
        // and skip every write queued behind it.
      }
    });
  }, []);

  const adoptServerPreference = useCallback(
    (user: { id: string; theme?: UserTheme } | null) => {
      if (user === null) {
        signedInAs.current = null;
        return;
      }
      const sameUser = signedInAs.current === user.id;
      signedInAs.current = user.id;
      if (sameUser && chosenHere.current) return;
      // A different account, so nothing this page chose applies to it.
      if (!sameUser) chosenHere.current = false;
      setPreferenceState(user.theme ?? 'system');
    },
    [],
  );

  const toggle = useCallback(() => {
    setPreference(resolveTheme(preference, system) === 'dark' ? 'light' : 'dark');
  }, [preference, setPreference, system]);


  const value = useMemo(
    () => ({ theme, preference, setPreference, toggle, adoptServerPreference }),
    [theme, preference, setPreference, toggle, adoptServerPreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside ThemeProvider');
  return ctx;
}
